import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { CONTACT_PLACEHOLDERS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  "I need housing help",
  "I am a homeowner with questions",
  "Partnership / community organization",
  "Media inquiry",
  "General question",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry_type: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        inquiry_type: form.inquiry_type || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Mahalo — your message has been received.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", inquiry_type: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testIdPrefix="contact-hero"
        eyebrow="Contact Us"
        title="Get in touch."
        subtitle="Tell us your story. We listen first — no pressure, no obligation. Every conversation starts with respect."
      />

      <section className="bg-brand-ivory py-20 md:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Contact Info */}
          <aside className="lg:col-span-4 lg:sticky lg:top-32" data-testid="contact-info">
            <p className="eyebrow text-brand-gold">Reach Us</p>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              Aloha. <br />We're here to listen.
            </h2>
            <p className="mt-6 text-brand-ink/70 leading-relaxed font-light">
              Whether you're navigating a difficult situation, exploring partnerships, or simply
              learning about our mission — we welcome your message.
            </p>

            <ul className="mt-10 space-y-6">
              <li className="flex items-start gap-4" data-testid="contact-info-address">
                <span className="w-10 h-10 flex items-center justify-center bg-brand-forest/5 rounded-sm shrink-0">
                  <MapPin size={18} className="text-brand-forest" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-ink/50">Office</p>
                  <p className="mt-1 text-brand-ink">{CONTACT_PLACEHOLDERS.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-info-phone">
                <span className="w-10 h-10 flex items-center justify-center bg-brand-forest/5 rounded-sm shrink-0">
                  <Phone size={18} className="text-brand-forest" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-ink/50">Phone</p>
                  <a href={`tel:${CONTACT_PLACEHOLDERS.phone}`} className="mt-1 block text-brand-ink hover:text-brand-forest transition-colors">
                    {CONTACT_PLACEHOLDERS.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-info-email">
                <span className="w-10 h-10 flex items-center justify-center bg-brand-forest/5 rounded-sm shrink-0">
                  <Mail size={18} className="text-brand-forest" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-ink/50">Email</p>
                  <a href={`mailto:${CONTACT_PLACEHOLDERS.email}`} className="mt-1 block text-brand-ink hover:text-brand-forest transition-colors break-all">
                    {CONTACT_PLACEHOLDERS.email}
                  </a>
                </div>
              </li>
            </ul>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="bg-white border border-brand-forest/15 p-10 md:p-14 rounded-sm"
              >
                <p className="eyebrow text-brand-gold">Mahalo</p>
                <h3 className="mt-5 font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
                  Your message is in our hands.
                </h3>
                <p className="mt-6 text-brand-ink/70 leading-relaxed font-light">
                  A member of our team will be in touch soon. In the meantime, feel free to explore
                  our mission and the solutions we offer.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another-btn"
                  className="mt-8 inline-flex items-center justify-center px-6 py-3 border border-brand-forest text-brand-forest text-[12px] uppercase tracking-[0.22em] font-semibold rounded-sm hover:bg-brand-forest hover:text-white transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-white border border-brand-forest/10 p-8 md:p-12 rounded-sm space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      required
                      placeholder="Your name"
                      className="mt-2 h-12 rounded-sm border-brand-forest/15 focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      placeholder="you@example.com"
                      className="mt-2 h-12 rounded-sm border-brand-forest/15 focus-visible:ring-brand-forest"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(optional)"
                      className="mt-2 h-12 rounded-sm border-brand-forest/15 focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inquiry_type" className="text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold">
                      Inquiry Type
                    </Label>
                    <Select
                      value={form.inquiry_type}
                      onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}
                    >
                      <SelectTrigger
                        id="inquiry_type"
                        data-testid="contact-select-inquiry-type"
                        className="mt-2 h-12 rounded-sm border-brand-forest/15 focus:ring-brand-forest"
                      >
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {INQUIRY_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`inquiry-option-${t.split(" ").join("-").toLowerCase()}`}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold">
                    Subject
                  </Label>
                  <Input
                    id="subject"
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Brief subject line"
                    className="mt-2 h-12 rounded-sm border-brand-forest/15 focus-visible:ring-brand-forest"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold">
                    Your Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    required
                    placeholder="Tell us a little about your situation or how we can help…"
                    rows={6}
                    className="mt-2 rounded-sm border-brand-forest/15 focus-visible:ring-brand-forest resize-none"
                  />
                </div>

                <div className="pt-2 flex items-center justify-between flex-wrap gap-4">
                  <p className="text-xs text-brand-ink/50 max-w-md">
                    Your information stays private. We use it only to respond to your message.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-brand-forest text-white text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-brand-forestDark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Message <Send size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
